import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, CheckIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { UserIcon } from '~/components/screens/forms/components_forms_creator/Icons';

// Extended ProfileAttribute interface to match backend response
interface ProfileAttribute {
  _id?: string;
  id?: string;
  name: string;
  dataType: 'text' | 'number' | 'boolean' | 'date' | 'closelist' | 'openlist';
  description?: string;
  useAI: boolean;
  closelist?: {
    options: string[];
    multiple: boolean;
  };
}

interface AttributeFieldProps {
  attribute: ProfileAttribute;
  attributeId: string;
  value: any;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: (attributeId: string) => void;
  onCancel: (attributeId: string) => void;
  onSave: (attributeId: string) => void;
  onChange: (attributeId: string, value: any) => void;
  displayMode?: 'inline' | 'card';
  showAIIndicator?: boolean;
  showTypeIndicator?: boolean;
  updatedAt?: string;
}

const AttributeField: React.FC<AttributeFieldProps> = ({
  attribute,
  attributeId,
  value,
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSave,
  onChange,
  displayMode = 'card',
  showAIIndicator = true,
  showTypeIndicator = true,
  updatedAt
}) => {
  const [t] = useTranslation('customer');

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get display name for attribute type
  const getAttributeTypeDisplay = () => {
    if (attribute.dataType === 'closelist' && attribute.closelist?.multiple) {
      return 'multilista';
    }
    return attribute.dataType;
  };

  // Render the input/display based on attribute type
  const renderAttributeInput = () => {
    if (isEditing) {
      switch (attribute.dataType) {
        case 'boolean':
          return (
            <div className="flex items-center gap-2">
              <label className="label cursor-pointer justify-start gap-3 m-0">
                <input
                  type="checkbox"
                  className={`toggle toggle-primary ${displayMode === 'inline' ? 'toggle-sm' : ''}`}
                  checked={value === true || value === 'true'}
                  onChange={(e) => onChange(attributeId, e.target.checked)}
                  disabled={isSaving}
                />
                <span className={`label-text ${displayMode === 'inline' ? 'text-sm' : 'font-medium'} ${displayMode === 'card' ? 'pl-2' : ''}`}>
                  {(value === true || value === 'true') ? t('yes', 'Sí') : t('no', 'No')}
                </span>
              </label>
            </div>
          );
        case 'number':
          return (
            <div className="flex items-center gap-2">
              <input
                type="number"
                className={`input input-bordered w-full ${displayMode === 'inline' ? 'input-sm' : 'input-md'}`}
                value={value || ''}
                onChange={(e) => onChange(attributeId, parseFloat(e.target.value) || 0)}
                disabled={isSaving}
                placeholder={t('enter_number', 'Ingresa un número...')}
              />
            </div>
          );
        case 'date':
          return (
            <div className="flex items-center gap-2">
              <input
                type="date"
                className={`input input-bordered w-full ${displayMode === 'inline' ? 'input-sm' : 'input-md'}`}
                value={value || ''}
                onChange={(e) => onChange(attributeId, e.target.value)}
                disabled={isSaving}
              />
            </div>
          );
        case 'closelist':
          if (attribute.closelist) {
            // If multiple selection is allowed, show checkboxes
            if (attribute.closelist.multiple) {
              const selectedValues = Array.isArray(value) ? value : 
                (value ? [value] : []);
              
              return (
                <div className="space-y-2">
                  <div className="text-sm text-brand-body/70 mb-2">
                    {t('select_multiple', 'Selecciona una o más opciones')} (multilista):
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                    {attribute.closelist.options.map((option) => (
                      <label key={option} className="label cursor-pointer justify-start gap-3 m-0">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={selectedValues.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onChange(attributeId, [...selectedValues, option]);
                            } else {
                              onChange(attributeId, selectedValues.filter((v: string) => v !== option));
                            }
                          }}
                          disabled={isSaving}
                        />
                        <span className="label-text text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            } else {
              // Single selection - show radio buttons
              return (
                <div className="space-y-2">
                  <div className="text-sm text-brand-body/70 mb-2">
                    {t('select_one', 'Selecciona una opción')} (opción única):
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                    {attribute.closelist.options.map((option) => (
                      <label key={option} className="label cursor-pointer justify-start gap-3 m-0">
                        <input
                          type="radio"
                          name={`${attributeId}-radio`}
                          className="radio radio-primary radio-sm"
                          checked={value === option}
                          onChange={() => onChange(attributeId, option)}
                          disabled={isSaving}
                        />
                        <span className="label-text text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            }
          }
          // Fall through to text input if no closelist options
        case 'openlist':
          const listValues = Array.isArray(value) ? value : (value ? value.split(',').map((v: string) => v.trim()) : []);
          return (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {listValues.map((item: string, index: number) => (
                  <div key={index} className="badge bg-brand-primary/10 text-brand-primary badge-sm gap-1">
                    {item}
                    <button
                      type="button"
                      className="hover:text-brand-coral"
                      onClick={() => {
                        const newValues = listValues.filter((_: any, i: number) => i !== index);
                        onChange(attributeId, newValues);
                      }}
                      disabled={isSaving}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className={`input input-bordered flex-1 ${displayMode === 'inline' ? 'input-sm' : 'input-md'}`}
                  placeholder={t('add_new_value', 'Agregar nuevo valor')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                      const newValue = e.currentTarget.value.trim();
                      if (!listValues.includes(newValue)) {
                        const newValues = [...listValues, newValue];
                        onChange(attributeId, newValues);
                        e.currentTarget.value = '';
                      }
                    }
                  }}
                  disabled={isSaving}
                />
                <button
                  type="button"
                  className={`btn btn-primary ${displayMode === 'inline' ? 'btn-sm' : 'btn-md'}`}
                  onClick={(e) => {
                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                    const newValue = input.value.trim();
                    if (newValue && !listValues.includes(newValue)) {
                      const newValues = [...listValues, newValue];
                      onChange(attributeId, newValues);
                      input.value = '';
                    }
                  }}
                  disabled={isSaving}
                >
                  {t('add', 'Agregar')}
                </button>
              </div>
            </div>
          );
        case 'text':
        default:
          return (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className={`input input-bordered w-full ${displayMode === 'inline' ? 'input-sm' : 'input-md'}`}
                value={value || ''}
                onChange={(e) => onChange(attributeId, e.target.value)}
                disabled={isSaving}
                placeholder={t('enter_text', 'Escribe aquí...')}
              />
            </div>
          );
      }
    }

    // Display mode
    if (value !== undefined && value !== null && value !== '') {
      let displayValue = value;
      if (attribute.dataType === 'boolean') {
        displayValue = value ? t('yes', 'Sí') : t('no', 'No');
      } else if (Array.isArray(value)) {
        displayValue = value.join(', ');
      } else {
        displayValue = value.toString();
      }
      
      return (
        <p className={`${displayMode === 'inline' ? 'text-sm text-base-content/70' : 'text-brand-body'}`}>
          {displayValue}
        </p>
      );
    } else {
      return (
        <p className={`italic ${displayMode === 'inline' ? 'text-sm text-base-content/50' : 'text-brand-body/50'}`}>
          {t('no_value', 'Sin valor')}
        </p>
      );
    }
  };

  // Render action buttons
  const renderActionButtons = () => {
    if (!isEditing) {
      return (
        <button
          className={`btn btn-ghost p-1 hover:bg-primary hover:text-primary-content ${
            displayMode === 'inline' ? 'btn-xs' : 'btn-sm'
          }`}
          onClick={() => onEdit(attributeId)}
          title={t('edit', 'Editar')}
        >
          <PencilIcon className={`${displayMode === 'inline' ? 'h-4 w-4' : 'h-5 w-5'}`} />
        </button>
      );
    }

    return (
      <div className="flex gap-1">
        <button
          className={`btn btn-primary ${displayMode === 'inline' ? 'btn-xs' : 'btn-sm'} ${isSaving ? 'loading' : ''}`}
          onClick={() => onSave(attributeId)}
          disabled={isSaving}
          title={t('save', 'Guardar')}
        >
          {!isSaving && <CheckIcon className={`${displayMode === 'inline' ? 'h-3 w-3' : 'h-4 w-4'}`} />}
        </button>
        <button
          className={`btn btn-ghost text-error hover:bg-error hover:text-error-content ${
            displayMode === 'inline' ? 'btn-xs' : 'btn-sm'
          }`}
          onClick={() => onCancel(attributeId)}
          disabled={isSaving}
          title={t('cancel', 'Cancelar')}
        >
          <XMarkIcon className={`${displayMode === 'inline' ? 'h-3 w-3' : 'h-4 w-4'}`} />
        </button>
      </div>
    );
  };

  // Inline mode (for CustomerProfile)
  if (displayMode === 'inline') {
    // Special layout for openlist fields to align action buttons with "Agregar" button
    if (isEditing && attribute.dataType === 'openlist') {
      const listValues = Array.isArray(value) ? value : (value ? value.split(',').map((v: string) => v.trim()) : []);
      
      return (
        <div className="p-2 bg-base-200 rounded">
          <div className="flex justify-between items-start mb-1">
            <p className="font-medium text-sm">{attribute.name}</p>
          </div>
          <div className="space-y-2">
            {listValues.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {listValues.map((item: string, index: number) => (
                  <div key={index} className="badge bg-brand-primary/10 text-brand-primary badge-sm gap-1">
                    {item}
                    <button
                      type="button"
                      className="hover:text-brand-coral"
                      onClick={() => {
                        const newValues = listValues.filter((_: any, i: number) => i !== index);
                        onChange(attributeId, newValues);
                      }}
                      disabled={isSaving}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="input input-bordered flex-1 input-sm"
                placeholder={t('add_new_value', 'Agregar nuevo valor')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                    const newValue = e.currentTarget.value.trim();
                    if (!listValues.includes(newValue)) {
                      const newValues = [...listValues, newValue];
                      onChange(attributeId, newValues);
                      e.currentTarget.value = '';
                    }
                  }
                }}
                disabled={isSaving}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                  const newValue = input.value.trim();
                  if (newValue && !listValues.includes(newValue)) {
                    const newValues = [...listValues, newValue];
                    onChange(attributeId, newValues);
                    input.value = '';
                  }
                }}
                disabled={isSaving}
              >
                {t('add', 'Agregar')}
              </button>
              {renderActionButtons()}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-2 bg-base-200 rounded">
        <div className="flex justify-between items-start mb-1">
          <p className="font-medium text-sm">{attribute.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            {renderAttributeInput()}
          </div>
          {renderActionButtons()}
        </div>
      </div>
    );
  }

  // Card mode (for CustomerAttributes)
  return (
    <div className="card-brand border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="card-body p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-brand-heading">{attribute.name}</h4>
            {showAIIndicator && (
              <div 
                className={`flex items-center justify-center w-5 h-5 rounded-full tooltip ${
                  attribute.useAI 
                    ? 'bg-blue-100 text-blue-600 tooltip-bottom' 
                    : 'bg-gray-100 text-gray-600 tooltip-bottom'
                }`}
                data-tip={attribute.useAI ? 'Llenado automáticamente por IA' : 'Llenado manualmente por humanos'}
              >
                {attribute.useAI ? <SparklesIcon className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showTypeIndicator && (
              <span className="badge bg-brand-accent/20 text-brand-primary badge-sm">
                {getAttributeTypeDisplay()}
              </span>
            )}
            {renderActionButtons()}
          </div>
        </div>
        <div className="mb-2">
          {renderAttributeInput()}
        </div>
        {updatedAt && (
          <p className="text-xs text-brand-body/50">
            {t('updated', 'Actualizado')}: {formatDate(updatedAt)}
          </p>
        )}
      </div>
    </div>
  );
};

export default AttributeField;
